import React, { useEffect, useState, useRef } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Pagination,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    OutlinedInput,
    Chip
} from "@mui/material";
import { PrintOutlined } from "@mui/icons-material";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";

const PAGE_SIZE = 25;

function Headwisestatement() {
    const [expenseHeads, setExpenseHeads] = useState([]);
    const [incomeHeads, setIncomeHeads] = useState([]);
    const [ledger, setLedger] = useState([]);
    const [filteredLedger, setFilteredLedger] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filters State
    const [fromDate, setFromDate] = useState(
        new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split("T")[0]
    ); 
    const [toDate, setToDate] = useState(new Date().toISOString().split("T")[0]);
    
    // Changed to an Array to support multiple head selections
    const [selectedHeadIds, setSelectedHeadIds] = useState([]);
    
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);

    // Totals State
    const [totals, setTotals] = useState({ credit: 0, debit: 0, balance: 0 });

    const printRef = useRef();

    // Helper map to find Head Names easily by ID
    const [headMap, setHeadMap] = useState({});

    // ================= 1. Load Everything Sequentially =================
    useEffect(() => {
        const fetchAllStatementData = async () => {
            setLoading(true);
            try {
                // Fetch Categories
                const expHeadsSnap = await getDocs(collection(db, "expenseHeads"));
                const incHeadsSnap = await getDocs(collection(db, "incomeHeads"));

                const expHeadsArr = expHeadsSnap.docs.map(d => ({ id: d.id, ...d.data(), origin: "Expense" }));
                const incHeadsArr = incHeadsSnap.docs.map(d => ({ id: d.id, ...d.data(), origin: "Income" }));

                setExpenseHeads(expHeadsArr);
                setIncomeHeads(incHeadsArr);

                // Create a quick lookup map for names
                const mapping = {};
                expHeadsArr.forEach(h => mapping[h.id] = h.name);
                incHeadsArr.forEach(h => mapping[h.id] = h.name);
                setHeadMap(mapping);

                // Fetch Action Logs
                const expensesSnap = await getDocs(collection(db, "otherExpenses"));
                const incomeSnap = await getDocs(collection(db, "otherIncome"));

                const structuredExpenses = expensesSnap.docs.map(d => {
                    const data = d.data();
                    const matchedHead = expHeadsArr.find(h => h.id === data.expenseHeadId);
                    return {
                        id: d.id,
                        headId: data.expenseHeadId,
                        headName: matchedHead?.name || "Unknown Expense Head",
                        date: data.date?.toDate() || new Date(),
                        amount: Number(data.amount) || 0,
                        type: "Debit",
                        description: data.description || "-",
                    };
                });

                const structuredIncome = incomeSnap.docs.map(d => {
                    const data = d.data();
                    const matchedHead = incHeadsArr.find(h => h.id === data.incomeHeadId);
                    return {
                        id: d.id,
                        headId: data.incomeHeadId,
                        headName: matchedHead?.name || "Unknown Income Head",
                        date: data.date?.toDate() || new Date(),
                        amount: Number(data.amount) || 0,
                        type: "Credit",
                        description: data.description || "-",
                    };
                });

                // Merge chronologically
                const compiledLedger = [...structuredExpenses, ...structuredIncome].sort(
                    (a, b) => a.date - b.date
                );

                setLedger(compiledLedger);
            } catch (err) {
                console.error("Error building report structure:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchAllStatementData();
    }, []);

    // ================= 2. Unified Calculation Matrix Engine =================
    useEffect(() => {
        let runningData = [...ledger];

        // Date Boundary Filter
        if (fromDate) {
            const startPoint = new Date(fromDate);
            startPoint.setHours(0, 0, 0, 0);
            runningData = runningData.filter(item => item.date >= startPoint);
        }
        if (toDate) {
            const endPoint = new Date(toDate);
            endPoint.setHours(23, 59, 59, 999);
            runningData = runningData.filter(item => item.date <= endPoint);
        }

        // Account Master Filter Matrix (Supports Array Matches Now)
        if (selectedHeadIds.length > 0) {
            runningData = runningData.filter(item => selectedHeadIds.includes(item.headId));
        }

        // String Query Parser
        if (search.trim() !== "") {
            const queryStr = search.toLowerCase();
            runningData = runningData.filter(
                item =>
                    item.headName.toLowerCase().includes(queryStr) ||
                    item.description.toLowerCase().includes(queryStr) ||
                    item.type.toLowerCase().includes(queryStr) ||
                    item.amount.toString().includes(queryStr)
            );
        }

        setFilteredLedger(runningData);
        setPage(1); 

        // Compute Financial Aggregations
        let totalCredit = 0;
        let totalDebit = 0;

        runningData.forEach(tx => {
            if (tx.type === "Credit") totalCredit += tx.amount;
            if (tx.type === "Debit") totalDebit += tx.amount;
        });

        setTotals({
            credit: totalCredit,
            debit: totalDebit,
            balance: totalCredit - totalDebit,
        });
    }, [ledger, fromDate, toDate, selectedHeadIds, search]);

    const paginatedItems = filteredLedger.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const handleHeadChange = (event) => {
        const { target: { value } } = event;
        // On autofill we get a stringified value.
        setSelectedHeadIds(typeof value === 'string' ? value.split(',') : value);
    };

    // ================= 3. Print Layout Generator =================
    const handlePrintStatement = () => {
        const printableContent = printRef.current.innerHTML;
        const printWindow = window.open("", "_blank", "width=900,height=700");
        
        printWindow.document.write(`
            <html>
                <head>
                    <title>Merged Multi-Head Report</title>
                    <style>
                        body { font-family: sans-serif; padding: 25px; color: #222; }
                        .header { text-align: center; border-bottom: 3px double #333; padding-bottom: 12px; margin-bottom: 20px; }
                        .header h2 { margin: 0; text-transform: uppercase; font-size: 22px; }
                        .meta-info { display: flex; justify-content: space-between; font-size: 13px; margin-bottom: 15px; background: #f1f3f5; padding: 8px; border: 1px solid #ddd; }
                        table { width: 100%; border-collapse: collapse; font-size: 12px; }
                        th, td { border: 1px solid #666; padding: 8px 6px; text-align: left; }
                        th { background-color: #f8f9fa; font-weight: bold; text-transform: uppercase; }
                        .text-right { text-align: right; }
                        .font-weight-bold { font-weight: bold; }
                        .bg-summary { background-color: #f1f3f5; }
                        .credit-text { color: #2e7d32; }
                        .debit-text { color: #c62828; }
                    </style>
                </head>
                <body>
                    ${printableContent}
                    <script>
                        window.onload = function() {
                            window.print();
                            setTimeout(function() { window.close(); }, 500);
                        };
                    </script>
                </body>
            </html>
        `);
        printWindow.document.close();
    };

    return (
        <Box p={2} bgcolor="#f4f6f8" minHeight="100vh">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5" fontWeight={800}>
                    Multi-Head Account Statement
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<PrintOutlined />}
                    onClick={handlePrintStatement}
                    disabled={loading || filteredLedger.length === 0}
                >
                    Print Report
                </Button>
            </Box>

            {/* Filter UI panel */}
            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr", md: "1.2fr 1.2fr 1.6fr 1fr" }} gap={2}>
                        <TextField
                            label="From Date"
                            type="date"
                            size="small"
                            value={fromDate}
                            onChange={e => setFromDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <TextField
                            label="To Date"
                            type="date"
                            size="small"
                            value={toDate}
                            onChange={e => setToDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        
                        {/* MULTI-SELECT SYSTEM FOR HEADS */}
                        <FormControl fullWidth size="small">
                            <InputLabel id="multiple-heads-label">Select Account Heads</InputLabel>
                            <Select
                                labelId="multiple-heads-label"
                                multiple
                                value={selectedHeadIds}
                                onChange={handleHeadChange}
                                input={<OutlinedInput label="Select Account Heads" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((value) => (
                                            <Chip key={value} size="small" label={headMap[value] || value} />
                                        ))}
                                    </Box>
                                )}
                            >
                                <MenuItem disabled sx={{ fontWeight: "bold", color: "#c62828", opacity: 1 }}>
                                    -- EXPENSE TRACKS --
                                </MenuItem>
                                {expenseHeads.map((h) => (
                                    <MenuItem key={h.id} value={h.id}>
                                        <Checkbox checked={selectedHeadIds.indexOf(h.id) > -1} />
                                        <ListItemText primary={h.name} />
                                    </MenuItem>
                                ))}
                                
                                <MenuItem disabled sx={{ fontWeight: "bold", color: "#2e7d32", opacity: 1 }}>
                                    -- INCOME TRACKS --
                                </MenuItem>
                                {incomeHeads.map((h) => (
                                    <MenuItem key={h.id} value={h.id}>
                                        <Checkbox checked={selectedHeadIds.indexOf(h.id) > -1} />
                                        <ListItemText primary={h.name} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            label="Search"
                            placeholder="Search descriptions..."
                            size="small"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            fullWidth
                        />
                    </Box>
                </CardContent>
            </Card>

            {/* Primary Ledger Data Grid */}
            <Card>
                <CardContent>
                    {loading ? (
                        <Typography align="center" py={3}>Assembling combined records...</Typography>
                    ) : filteredLedger.length === 0 ? (
                        <Typography align="center" py={3}>No statements match selected heads or range inputs.</Typography>
                    ) : (
                        <>
                            <Table size="small">
                                <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Account Head</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }} align="right">Income / Credit (₹)</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }} align="right">Expense / Debit (₹)</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedItems.map((row, idx) => (
                                        <TableRow key={row.id}>
                                            <TableCell>{(page - 1) * PAGE_SIZE + idx + 1}</TableCell>
                                            <TableCell>{row.date.toLocaleDateString()}</TableCell>
                                            <TableCell sx={{ fontWeight: 600 }}>{row.headName}</TableCell>
                                            <TableCell>{row.description}</TableCell>
                                            <TableCell align="right" sx={{ color: "#2e7d32", fontWeight: 500 }}>
                                                {row.type === "Credit" ? row.amount.toFixed(2) : "-"}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: "#c62828", fontWeight: 500 }}>
                                                {row.type === "Debit" ? row.amount.toFixed(2) : "-"}
                                            </TableCell>
                                        </TableRow>
                                    ))}

                                    {/* Table Bottom Persistent Totals Segment */}
                                    <TableRow sx={{ backgroundColor: "#eaeffa", "& td": { fontWeight: 700 } }}>
                                        <TableCell colSpan={4} align="right">TOTAL INCOME & EXPENSE COLUMNS:</TableCell>
                                        <TableCell align="right" sx={{ color: "#2e7d32" }}>₹{totals.credit.toFixed(2)}</TableCell>
                                        <TableCell align="right" sx={{ color: "#c62828" }}>₹{totals.debit.toFixed(2)}</TableCell>
                                    </TableRow>
                                    <TableRow sx={{ backgroundColor: "#e2eafc", "& td": { fontWeight: 800 } }}>
                                        <TableCell colSpan={4} align="right">NET PROFIT / CASH POSITION:</TableCell>
                                        <TableCell colSpan={2} align="center" sx={{ color: totals.balance >= 0 ? "#1565c0" : "#c62828" }}>
                                            ₹{totals.balance.toFixed(2)}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>

                            <Box display="flex" justifyContent="center" mt={2}>
                                <Pagination
                                    count={Math.ceil(filteredLedger.length / PAGE_SIZE)}
                                    page={page}
                                    onChange={(e, v) => setPage(v)}
                                />
                            </Box>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* ================= INVISIBLE PRINT EXPORT CONTAINER DOCUMENT ================= */}
            <div style={{ display: "none" }}>
                <div ref={printRef}>
                    <div className="header">
                        <h2>Merged Financial Statement Report</h2>
                    </div>
                    <div className="meta-info">
                        <div><strong>Statement Duration:</strong> {fromDate || "Initial Log"} to {toDate || "Present"}</div>
                        <div><strong>Selected Heads:</strong> {selectedHeadIds.length === 0 ? "All Heads Selected" : selectedHeadIds.map(id => headMap[id]).join(", ")}</div>
                    </div>
                    <table>
                        <thead>
                            <tr>
                                <th style={{ width: "5%" }}>#</th>
                                <th style={{ width: "13%" }}>Date</th>
                                <th style={{ width: "25%" }}>Account Head</th>
                                <th style={{ width: "27%" }}>Description</th>
                                <th style={{ width: "15%" }} className="text-right">Income / Credit (₹)</th>
                                <th style={{ width: "15%" }} className="text-right">Expense / Debit (₹)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredLedger.map((row, idx) => (
                                <tr key={row.id}>
                                    <td>{idx + 1}</td>
                                    <td>{row.date.toLocaleDateString()}</td>
                                    <td><strong>{row.headName}</strong></td>
                                    <td>{row.description}</td>
                                    <td className="text-right credit-text">{row.type === "Credit" ? row.amount.toFixed(2) : "-"}</td>
                                    <td className="text-right debit-text">{row.type === "Debit" ? row.amount.toFixed(2) : "-"}</td>
                                </tr>
                            ))}
                            <tr className="bg-summary font-weight-bold">
                                <td colSpan={4} className="text-right">TOTAL INCOME & EXPENSE COLUMNS:</td>
                                <td className="text-right credit-text">₹{totals.credit.toFixed(2)}</td>
                                <td className="text-right debit-text">₹{totals.debit.toFixed(2)}</td>
                            </tr>
                            <tr className="bg-summary font-weight-bold" style={{ fontSize: "13px" }}>
                                <td colSpan={4} className="text-right">NET CLOSING POSITION VALUE:</td>
                                <td colSpan={2} style={{ textAlign: "center", color: totals.balance >= 0 ? "#1565c0" : "#c62828" }}>
                                    ₹{totals.balance.toFixed(2)}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </Box>
    );
}

export default Headwisestatement;